import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  Users,
  ArrowLeftRight,
  TrendingUp,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Crown,
  Wallet,
  BarChart3,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { api } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';

interface DashboardStats {
  tenants: {
    total: number;
    active: number;
    suspended: number;
  };
  users: {
    total: number;
  };
  accounts: {
    total: number;
  };
  transactions: {
    today: { count: number; volume: number; fees: number };
    week: { count: number; volume: number; fees: number };
    month: { count: number; volume: number; fees: number };
    trend: number;
    trendDirection: 'UP' | 'DOWN' | 'STABLE';
  };
  providerHealth: {
    healthy: number;
    degraded: number;
    down: number;
  };
}

interface RevenueSummary {
  today: { value: number; trend: number; trendDirection: 'UP' | 'DOWN' };
  week: { value: number; trend: number; trendDirection: 'UP' | 'DOWN' };
  month: { value: number; trend: number; trendDirection: 'UP' | 'DOWN' };
}

interface Alert {
  id: string;
  type: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  message: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

interface FeeCoverage {
  totalOperationTypes: number;
  configuredOperationTypes: number;
  coveragePercentage: number;
  missingOperations: string[];
  tenantCoverage: {
    total: number;
    withFees: number;
    percentage: number;
  };
}

interface TopTenant {
  rank: number;
  tenantId: string;
  tenantName: string;
  status: string;
  transactionCount: number;
  volume: number;
  fees: number;
  percentage: number;
}

interface TenantVolume {
  tenantId: string;
  tenantName: string;
  volume: number;
  transactionCount: number;
  percentage: number;
}

interface HealthStatus {
  providers: Array<{
    name: string;
    status: 'healthy' | 'degraded' | 'down';
    latency: number;
    lastCheck: string;
  }>;
  overall: 'healthy' | 'degraded' | 'down';
}

interface ProviderHealthOverview {
  templates: Array<{
    templateId: string;
    templateName: string;
    category: string;
    type: string;
    totalInstances: number;
    activeInstances: number;
    errorInstances: number;
    avgResponseTime: number;
    totalRequests: number;
  }>;
  totalInstances: number;
  activeInstances: number;
  errorInstances: number;
  recentErrors: Array<{
    instanceId: string;
    instanceName: string;
    tenantName: string;
    templateName: string;
    error: string;
    errorAt: string;
  }>;
}

interface RevenueDataPoint {
  date: string;
  revenue: number;
  platformFee: number;
  providerCost: number;
  netRevenue: number;
}

interface VolumeDataPoint {
  date: string;
  volume: number;
  transactions: number;
}

interface CategoryDistribution {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number;
}

const CHART_COLORS = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#ec4899'];

const periodOptions = [
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: '12m', label: 'Últimos 12 meses' },
];

export function SuperAdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [revenueSummary, setRevenueSummary] = useState<RevenueSummary | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [feeCoverage, setFeeCoverage] = useState<FeeCoverage | null>(null);
  const [topTenants, setTopTenants] = useState<TopTenant[]>([]);
  const [providerHealth, setProviderHealth] = useState<ProviderHealthOverview | null>(null);
  const [tenantVolumes, setTenantVolumes] = useState<TenantVolume[]>([]);
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueDataPoint[]>([]);
  const [volumeData, setVolumeData] = useState<VolumeDataPoint[]>([]);
  const [categoryDistribution, setCategoryDistribution] = useState<CategoryDistribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('30d');

  const fetchData = async () => {
    try {
      // Fetch from new dashboard endpoints
      const [statsRes, revenueRes, alertsRes, feeCoverageRes, topTenantsRes, providerHealthRes] = await Promise.all([
        api.get('/super-admin/dashboard/stats').catch(() => null),
        api.get('/super-admin/dashboard/revenue').catch(() => null),
        api.get('/super-admin/dashboard/alerts').catch(() => null),
        api.get('/super-admin/dashboard/fee-coverage').catch(() => null),
        api.get('/super-admin/dashboard/top-tenants?limit=5').catch(() => null),
        api.get('/super-admin/dashboard/provider-health').catch(() => null),
      ]);

      if (statsRes?.data) setStats(statsRes.data);
      if (revenueRes?.data) setRevenueSummary(revenueRes.data);
      if (alertsRes?.data) setAlerts(alertsRes.data);
      if (feeCoverageRes?.data) setFeeCoverage(feeCoverageRes.data);
      if (topTenantsRes?.data) setTopTenants(topTenantsRes.data);
      if (providerHealthRes?.data) setProviderHealth(providerHealthRes.data);

      // Fallback to old endpoints if new ones fail
      if (!statsRes?.data) {
        const [dashboardRes, volumeRes, healthRes] = await Promise.all([
          api.get('/super-admin/dashboard').catch(() => null),
          api.get('/super-admin/transactions/by-tenant?period=month').catch(() => null),
          api.get('/super-admin/health').catch(() => null),
        ]);
        if (dashboardRes?.data) {
          // Convert old format to new format
          setStats({
            tenants: { total: dashboardRes.data.totalTenants || 0, active: dashboardRes.data.activeTenants || 0, suspended: 0 },
            users: { total: dashboardRes.data.totalUsers || 0 },
            accounts: { total: dashboardRes.data.totalAccounts || 0 },
            transactions: {
              today: { count: 0, volume: 0, fees: 0 },
              week: { count: 0, volume: 0, fees: 0 },
              month: { count: dashboardRes.data.totalTransactions || 0, volume: dashboardRes.data.totalVolume || 0, fees: 0 },
              trend: 0,
              trendDirection: 'STABLE',
            },
            providerHealth: dashboardRes.data.providerHealth || { healthy: 0, degraded: 0, down: 0 },
          });
        }
        if (volumeRes?.data?.tenants) setTenantVolumes(volumeRes.data.tenants);
        if (healthRes?.data) setHealthStatus(healthRes.data);
      }
      
      // Fetch chart data
      await fetchChartData();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchChartData = async () => {
    try {
      // Try to fetch from reports endpoint, fallback to mock data if not available
      const [revenueRes, volumeRes] = await Promise.all([
        api.get(`/reports/revenue?period=${period}`).catch(() => null),
        api.get(`/reports/transaction-volume?period=${period}`).catch(() => null),
      ]);

      if (revenueRes?.data?.data) {
        setRevenueData(revenueRes.data.data);
      } else {
        // Generate sample data based on period
        setRevenueData(generateSampleRevenueData(period));
      }

      if (volumeRes?.data?.data) {
        setVolumeData(volumeRes.data.data);
      } else {
        setVolumeData(generateSampleVolumeData(period));
      }

      // Category distribution
      setCategoryDistribution([
        { name: 'BAAS', value: 65, color: CHART_COLORS[0] },
        { name: 'CRYPTO', value: 20, color: CHART_COLORS[1] },
        { name: 'BENEFITS', value: 10, color: CHART_COLORS[2] },
        { name: 'CARDS', value: 5, color: CHART_COLORS[3] },
      ]);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const generateSampleRevenueData = (period: string): RevenueDataPoint[] => {
    const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 365;
    const data: RevenueDataPoint[] = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const baseRevenue = 5000 + Math.random() * 3000;
      const platformFee = baseRevenue * 0.015;
      const providerCost = baseRevenue * 0.005;
      
      data.push({
        date: date.toISOString().split('T')[0],
        revenue: Math.round(baseRevenue),
        platformFee: Math.round(platformFee),
        providerCost: Math.round(providerCost),
        netRevenue: Math.round(platformFee - providerCost),
      });
    }
    return data;
  };

  const generateSampleVolumeData = (period: string): VolumeDataPoint[] => {
    const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 365;
    const data: VolumeDataPoint[] = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      data.push({
        date: date.toISOString().split('T')[0],
        volume: Math.round(50000 + Math.random() * 30000),
        transactions: Math.round(50 + Math.random() * 100),
      });
    }
    return data;
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [period]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'degraded': return 'warning';
      case 'down': return 'error';
      default: return 'info';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'degraded': return <AlertTriangle className="w-4 h-4" />;
      case 'down': return <XCircle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
            <Crown className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">Dashboard Global</h1>
            <p className="text-surface-400">Visão geral da plataforma</p>
          </div>
        </div>
        <Button
          variant="secondary"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card variant="stat">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Total Tenants</p>
                <p className="text-3xl font-bold text-surface-100 mt-1">
                  {stats?.tenants?.total || 0}
                </p>
                <p className="text-xs text-green-400 mt-1">
                  {stats?.tenants?.active || 0} ativos
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary-400" />
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card variant="stat">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Total Usuários</p>
                <p className="text-3xl font-bold text-surface-100 mt-1">
                  {stats?.users?.total || 0}
                </p>
                <p className="text-xs text-surface-500 mt-1">
                  Em todos os tenants
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-accent-400" />
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card variant="stat">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Total Contas</p>
                <p className="text-3xl font-bold text-surface-100 mt-1">
                  {stats?.accounts?.total || 0}
                </p>
                <p className="text-xs text-surface-500 mt-1">
                  Bolsão + Virtuais + Reais
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card variant="stat">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Volume Mensal</p>
                <p className="text-3xl font-bold text-surface-100 mt-1">
                  {formatCurrency(stats?.transactions?.month?.volume || 0)}
                </p>
                <p className={`text-xs mt-1 flex items-center gap-1 ${
                  stats?.transactions?.trendDirection === 'UP' ? 'text-green-400' :
                  stats?.transactions?.trendDirection === 'DOWN' ? 'text-red-400' : 'text-surface-500'
                }`}>
                  {stats?.transactions?.trendDirection === 'UP' && <TrendingUp className="w-3 h-3" />}
                  {stats?.transactions?.trendDirection === 'DOWN' && <TrendingUp className="w-3 h-3 rotate-180" />}
                  {stats?.transactions?.month?.count || 0} transações
                  {stats?.transactions?.trend ? ` (${stats.transactions.trend > 0 ? '+' : ''}${stats.transactions.trend.toFixed(1)}%)` : ''}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <ArrowLeftRight className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Revenue Summary & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
        >
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-surface-100">
                Resumo de Receita
              </h2>
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-surface-800/50 text-center">
                <p className="text-xs text-surface-500 mb-1">Hoje</p>
                <p className="text-xl font-bold text-surface-100">
                  {formatCurrency(revenueSummary?.today?.value || 0)}
                </p>
                <div className={`flex items-center justify-center gap-1 text-xs mt-1 ${
                  revenueSummary?.today?.trendDirection === 'UP' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {revenueSummary?.today?.trendDirection === 'UP' ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingUp className="w-3 h-3 rotate-180" />
                  )}
                  {revenueSummary?.today?.trend?.toFixed(1) || 0}%
                </div>
              </div>
              <div className="p-4 rounded-lg bg-surface-800/50 text-center">
                <p className="text-xs text-surface-500 mb-1">Semana</p>
                <p className="text-xl font-bold text-surface-100">
                  {formatCurrency(revenueSummary?.week?.value || 0)}
                </p>
                <div className={`flex items-center justify-center gap-1 text-xs mt-1 ${
                  revenueSummary?.week?.trendDirection === 'UP' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {revenueSummary?.week?.trendDirection === 'UP' ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingUp className="w-3 h-3 rotate-180" />
                  )}
                  {revenueSummary?.week?.trend?.toFixed(1) || 0}%
                </div>
              </div>
              <div className="p-4 rounded-lg bg-surface-800/50 text-center">
                <p className="text-xs text-surface-500 mb-1">Mês</p>
                <p className="text-xl font-bold text-surface-100">
                  {formatCurrency(revenueSummary?.month?.value || 0)}
                </p>
                <div className={`flex items-center justify-center gap-1 text-xs mt-1 ${
                  revenueSummary?.month?.trendDirection === 'UP' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {revenueSummary?.month?.trendDirection === 'UP' ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingUp className="w-3 h-3 rotate-180" />
                  )}
                  {revenueSummary?.month?.trend?.toFixed(1) || 0}%
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Alerts Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.47 }}
        >
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-surface-100">
                Alertas do Sistema
              </h2>
              <Badge variant={alerts.some(a => a.severity === 'CRITICAL') ? 'error' : alerts.some(a => a.severity === 'WARNING') ? 'warning' : 'success'}>
                {alerts.length} alertas
              </Badge>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {alerts.length > 0 ? (
                alerts.slice(0, 5).map((alert) => (
                  <div
                    key={alert.id}
                    className={`p-3 rounded-lg border ${
                      alert.severity === 'CRITICAL' ? 'bg-red-500/10 border-red-500/30' :
                      alert.severity === 'WARNING' ? 'bg-yellow-500/10 border-yellow-500/30' :
                      'bg-blue-500/10 border-blue-500/30'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {alert.severity === 'CRITICAL' ? (
                        <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      ) : alert.severity === 'WARNING' ? (
                        <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Activity className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${
                          alert.severity === 'CRITICAL' ? 'text-red-300' :
                          alert.severity === 'WARNING' ? 'text-yellow-300' :
                          'text-blue-300'
                        }`}>
                          {alert.message}
                        </p>
                        <p className="text-xs text-surface-500 mt-1">
                          {new Date(alert.createdAt).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-surface-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                  <p>Nenhum alerta no momento</p>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Fee Coverage & Top Tenants */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fee Coverage Widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.48 }}
        >
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-surface-100">
                Cobertura de Taxas
              </h2>
              <Badge variant={
                (feeCoverage?.coveragePercentage || 0) >= 80 ? 'success' :
                (feeCoverage?.coveragePercentage || 0) >= 50 ? 'warning' : 'error'
              }>
                {feeCoverage?.coveragePercentage?.toFixed(0) || 0}%
              </Badge>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-surface-400">Operações Configuradas</span>
                  <span className="text-surface-100">
                    {feeCoverage?.configuredOperationTypes || 0} / {feeCoverage?.totalOperationTypes || 0}
                  </span>
                </div>
                <div className="h-3 bg-surface-800 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${feeCoverage?.coveragePercentage || 0}%` }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className={`h-full rounded-full ${
                      (feeCoverage?.coveragePercentage || 0) >= 80 ? 'bg-green-500' :
                      (feeCoverage?.coveragePercentage || 0) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                  />
                </div>
              </div>
              {feeCoverage?.missingOperations && feeCoverage.missingOperations.length > 0 && (
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-xs text-yellow-400 font-medium mb-1">Operações sem taxa:</p>
                  <p className="text-xs text-yellow-300">
                    {feeCoverage.missingOperations.join(', ')}
                  </p>
                </div>
              )}
              <div className="pt-2 border-t border-surface-700">
                <div className="flex justify-between text-sm">
                  <span className="text-surface-400">Tenants com taxas</span>
                  <span className="text-surface-100">
                    {feeCoverage?.tenantCoverage?.withFees || 0} / {feeCoverage?.tenantCoverage?.total || 0}
                    <span className="text-surface-500 ml-1">
                      ({feeCoverage?.tenantCoverage?.percentage?.toFixed(0) || 0}%)
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Top Tenants */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.49 }}
        >
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-surface-100">
                Top Tenants (Volume)
              </h2>
              <Crown className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="space-y-3">
              {topTenants.length > 0 ? (
                topTenants.map((tenant, index) => (
                  <div key={tenant.tenantId} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                          index === 0 ? 'bg-yellow-500 text-yellow-900' :
                          index === 1 ? 'bg-gray-400 text-gray-900' :
                          index === 2 ? 'bg-orange-600 text-orange-100' :
                          'bg-surface-700 text-surface-300'
                        }`}>
                          {tenant.rank}
                        </span>
                        <span className="text-surface-100 font-medium truncate max-w-[150px]">
                          {tenant.tenantName}
                        </span>
                      </div>
                      <span className="text-surface-300 text-sm font-medium">
                        {formatCurrency(tenant.volume)}
                      </span>
                    </div>
                    <div className="h-2 bg-surface-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${tenant.percentage}%` }}
                        transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                        className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
                      />
                    </div>
                    <p className="text-xs text-surface-500">
                      {tenant.transactionCount} transações • {tenant.percentage.toFixed(1)}% do total
                    </p>
                  </div>
                ))
              ) : tenantVolumes.length > 0 ? (
                tenantVolumes.slice(0, 5).map((tenant, index) => (
                  <div key={tenant.tenantId} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-surface-500 w-4">{index + 1}.</span>
                        <span className="text-surface-100 font-medium truncate max-w-[150px]">
                          {tenant.tenantName}
                        </span>
                      </div>
                      <span className="text-surface-300 text-sm">
                        {formatCurrency(tenant.volume)}
                      </span>
                    </div>
                    <div className="h-2 bg-surface-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${tenant.percentage}%` }}
                        transition={{ delay: 0.7 + index * 0.1, duration: 0.5 }}
                        className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
                      />
                    </div>
                    <p className="text-xs text-surface-500">
                      {tenant.transactionCount} transações ({tenant.percentage.toFixed(1)}%)
                    </p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-surface-500">
                  <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma transação no período</p>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Health Status & Volume by Tenant */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Provider Health */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-surface-100">
                Status dos Providers
              </h2>
              <Badge variant={getStatusColor(healthStatus?.overall || 'healthy')}>
                {healthStatus?.overall === 'healthy' ? 'Todos Operacionais' :
                 healthStatus?.overall === 'degraded' ? 'Degradado' : 'Problemas'}
              </Badge>
            </div>
            <div className="space-y-3">
              {healthStatus?.providers?.map((provider, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50"
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(provider.status)}
                    <div>
                      <p className="text-surface-100 font-medium">{provider.name}</p>
                      <p className="text-xs text-surface-500">
                        Latência: {provider.latency}ms
                      </p>
                    </div>
                  </div>
                  <Badge variant={getStatusColor(provider.status)} size="sm">
                    {provider.status}
                  </Badge>
                </div>
              )) || (
                <div className="text-center py-8 text-surface-500">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum provider configurado</p>
                </div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Volume by Tenant */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-surface-100">
                Volume por Tenant
              </h2>
              <BarChart3 className="w-5 h-5 text-surface-400" />
            </div>
            <div className="space-y-3">
              {tenantVolumes.length > 0 ? (
                tenantVolumes.slice(0, 5).map((tenant, index) => (
                  <div key={tenant.tenantId} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-surface-500 w-4">{index + 1}.</span>
                        <span className="text-surface-100 font-medium truncate max-w-[150px]">
                          {tenant.tenantName}
                        </span>
                      </div>
                      <span className="text-surface-300 text-sm">
                        {formatCurrency(tenant.volume)}
                      </span>
                    </div>
                    <div className="h-2 bg-surface-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${tenant.percentage}%` }}
                        transition={{ delay: 0.7 + index * 0.1, duration: 0.5 }}
                        className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
                      />
                    </div>
                    <p className="text-xs text-surface-500">
                      {tenant.transactionCount} transações ({tenant.percentage.toFixed(1)}%)
                    </p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-surface-500">
                  <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma transação no período</p>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Card>
          <h2 className="text-lg font-semibold text-surface-100 mb-4">
            Resumo de Saúde
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">Saudáveis</span>
              </div>
              <p className="text-3xl font-bold text-green-400">
                {stats?.providerHealth?.healthy || 0}
              </p>
              <p className="text-xs text-green-400/70 mt-1">providers operacionais</p>
            </div>
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-400 font-medium">Degradados</span>
              </div>
              <p className="text-3xl font-bold text-yellow-400">
                {stats?.providerHealth?.degraded || 0}
              </p>
              <p className="text-xs text-yellow-400/70 mt-1">providers com lentidão</p>
            </div>
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400 font-medium">Offline</span>
              </div>
              <p className="text-3xl font-bold text-red-400">
                {stats?.providerHealth?.down || 0}
              </p>
              <p className="text-xs text-red-400/70 mt-1">providers indisponíveis</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Period Selector */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="flex items-center justify-between"
      >
        <h2 className="text-xl font-semibold text-surface-100 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Análise de Período
        </h2>
        <Select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="w-48"
        >
          {periodOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </motion.div>

      {/* Revenue Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
      >
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-surface-100">
              Receita da Plataforma
            </h2>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-cyan-500" />
                <span className="text-surface-400">Taxa Plataforma</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-surface-400">Custo Provider</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-surface-400">Receita Líquida</span>
              </div>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorPlatformFee" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorNetRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#9ca3af"
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }}
                />
                <YAxis
                  stroke="#9ca3af"
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  tickFormatter={(value) => `R$${(value / 100).toFixed(0)}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#f3f4f6' }}
                  formatter={(value: number | undefined, name: string | undefined) => [
                    formatCurrency(value || 0),
                    name === 'platformFee' ? 'Taxa Plataforma' :
                    name === 'providerCost' ? 'Custo Provider' :
                    name === 'netRevenue' ? 'Receita Líquida' : (name || '')
                  ]}
                  labelFormatter={(label) => {
                    const date = new Date(label);
                    return date.toLocaleDateString('pt-BR');
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="platformFee"
                  stroke="#06b6d4"
                  fill="url(#colorPlatformFee)"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="providerCost"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                />
                <Area
                  type="monotone"
                  dataKey="netRevenue"
                  stroke="#10b981"
                  fill="url(#colorNetRevenue)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </motion.div>

      {/* Volume and Category Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transaction Volume Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
        >
          <Card>
            <h2 className="text-lg font-semibold text-surface-100 mb-4">
              Volume de Transações
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={volumeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="date"
                    stroke="#9ca3af"
                    tick={{ fill: '#9ca3af', fontSize: 10 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getDate()}/${date.getMonth() + 1}`;
                    }}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    tick={{ fill: '#9ca3af', fontSize: 10 }}
                    tickFormatter={(value) => `R$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: '#f3f4f6' }}
                    formatter={(value: number | undefined, name: string | undefined) => [
                      name === 'volume' ? formatCurrency(value || 0) : (value || 0),
                      name === 'volume' ? 'Volume' : 'Transações'
                    ]}
                    labelFormatter={(label) => {
                      const date = new Date(label);
                      return date.toLocaleDateString('pt-BR');
                    }}
                  />
                  <Bar dataKey="volume" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        {/* Category Distribution Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
        >
          <Card>
            <h2 className="text-lg font-semibold text-surface-100 mb-4">
              Distribuição por Categoria
            </h2>
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }: { name?: string; percent?: number }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {categoryDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number | undefined) => [`${value || 0}%`, 'Participação']}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => <span className="text-surface-300">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Transaction Count Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
      >
        <Card>
          <h2 className="text-lg font-semibold text-surface-100 mb-4">
            Quantidade de Transações
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={volumeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#9ca3af"
                  tick={{ fill: '#9ca3af', fontSize: 10 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }}
                />
                <YAxis
                  stroke="#9ca3af"
                  tick={{ fill: '#9ca3af', fontSize: 10 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#f3f4f6' }}
                  formatter={(value: number | undefined) => [value || 0, 'Transações']}
                  labelFormatter={(label) => {
                    const date = new Date(label);
                    return date.toLocaleDateString('pt-BR');
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="transactions"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ fill: '#f59e0b', strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5, fill: '#f59e0b' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
